import React, { useState, useEffect, useRef, useContext } from 'react';
import WindowWidthState from './WindowWidthState';
import { GlobalContext } from '../../api/GlobalContext';
import ConfirmationModal from './ConfirmationModal';
import Modal from '../Modals/Modal';
import { useMediaQuery } from 'react-responsive';
import { TableEditor } from './TableEditorComponents';
import { useLocation } from 'react-router-dom';

const EditableLabel = ({
  labelText,
  text,
  onChange,
  sectionText,
  configID,
  className = 'content',
  disabled,
  placeholder,
  section,
  removeBorder = false,
  isLibraryConfig = false,
  inputRef,
  children,
  forceShowSave = false,
  onSave,
  showSubsections = false,
  onToggleSubsections,
  subsections,
  setContent,
  filterSubsections,
  content,
  isSaving = false,
  ...rest
}) => {
  // Extract any external onClick passed from parents (e.g., handleViewOnlyClick)
  const { onClick: externalOnClick, ...restProps } = rest;
  // Gonna have to make a mirror of text and only ever push the text when we are done editing
  // This is so we can have the text be the same as the server
  // TODO: We will also need to make sure that the text is the same as the server when we start editing

  // Ref to track the current remoteText (Fixes the race condition on unmount)
  const remoteTextRef = useRef(text);
  useEffect(() => {
    remoteTextRef.current = text;
  }, [text]);

  // Track whether the user is actively editing to avoid clobbering unsaved changes
  const isEditingRef = useRef(false);

  const [localText, _setLocalText] = useState(text);
  const localTextRef = useRef(localText); // Ref to track the current localText (Fixes race condition on unmount)
  useEffect(() => {
    localTextRef.current = localText;
  }, [localText]);

  // If the text from outside changes, update localText
  // (but don’t overwrite it while the user is typing)
  useEffect(() => {
    // Extract TopLevelInstruction if text is a JSON object
    let extractedText = text;
    if (
      typeof text === 'string' &&
      text.startsWith('{') &&
      text.includes('TopLevelInstruction')
    ) {
      try {
        const parsed = JSON.parse(text);
        if (parsed.TopLevelInstruction) {
          extractedText = parsed.TopLevelInstruction;
        }
      } catch (error) {
        console.log('Failed to parse JSON, using original text');
      }
    }

    // Update localText when:
    // - the new text from JSON is not the same as the current text
    // - OR it's different from what we already have, with a length change bigger than 1
    // But never clobber unsaved changes while the user is actively typing
    if (
      !isEditingRef.current &&
      (extractedText !== text ||
        (extractedText !== localText &&
          Math.abs(extractedText.length - localText.length) > 1))
    ) {
      _setLocalText(extractedText);
    }
  }, [text]);

  // Listen for forceSave events from skip/no skip checkbox changes
  useEffect(() => {
    const handleForceSave = (event) => {
      if (event.detail && event.detail.content) {
        // Update local text to match the new content
        const parsedContent = JSON.parse(event.detail.content);
        _setLocalText(parsedContent.TopLevelInstruction || '');
        // Trigger save
        setTimeout(() => {
          handleSave();
        }, 50);
      }
    };

    document.addEventListener('forceSave', handleForceSave);
    return () => {
      document.removeEventListener('forceSave', handleForceSave);
    };
  }, []);

  // If it's an instruction offer then a button to reset the instruction witch will ai auto-gen the instruction
  const isInstruction = labelText === 'Instructions';
  const { apiClient } = useContext(GlobalContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emrFromUrl = queryParams.get('emr');

  const magicWand = isLibraryConfig ? null : (
    <i
      className="fa-duotone fa-solid fa-wand-magic-sparkles"
      style={{ fontSize: '1rem' }}
    ></i>
  );
  const [resetText, setResetText] = useState(magicWand);
  const [saveText, setSaveText] = useState('Save');

  useEffect(() => {
    if (isSaving) {
      setSaveText('Saving...');
    } else {
      setSaveText('Save');
    }
  }, [isSaving]);
  const [ConfirmModal, setConfirmModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const isSmallScreen = useMediaQuery({ query: '(max-width: 600px)' });

  const handleCancel = () => {
    setConfirmModal(false);
    setShowModal(false);
  };

  const handleConfirm = () => {
    autoGenInstruction();
    setConfirmModal(false);
  };

  const handleSave = () => {
    // Special handling if this is a top-level instruction with subsections
    if (isInstruction && subsections && setContent) {
      // Try to parse the current content as JSON so we can keep any existing subsections
      let currentSubsections = {};
      try {
        const parsedContent = JSON.parse(content || '{}');
        currentSubsections = parsedContent.SubSections || {};
      } catch (error) {
        currentSubsections = {}; // Fallback if content isn’t valid JSON
      }

      // Check if there are any subsections to preserve
      const hasSubsections =
        currentSubsections && Object.keys(currentSubsections).length > 0;

      // Build the new content:
      // - If subsections exist, wrap text + subsections into a JSON object
      // - If not, just save the plain text
      const newContent = hasSubsections
        ? JSON.stringify({
            TopLevelInstruction: localText,
            SubSections: currentSubsections,
          })
        : localText;

      setContent(newContent);

      // Call saveInstruction with the new content
      saveInstructionWithContent(newContent);
      return;
    }

    // Fallback: normal save without subsections
    saveInstruction();
  };

  async function saveInstruction() {
    setSaveText('Saving...');
    try {
      if (typeof onSave === 'function') {
        await onSave(localText, remoteTextRef.current);
      } else {
        const saveAction = isLibraryConfig
          ? apiClient.editLibraryConfigInstruction(
              configID,
              emrFromUrl,
              section,
              localText,
            )
          : apiClient.editConfig(
              configID,
              'update_question_value',
              sectionText,
              localText,
            );
        const data = await saveAction;
        if ('error' in data) {
          console.log('Error editing config');
          return;
        }
      }
      setSaveText('Save');
      onChange(localText, remoteTextRef.current);
    } catch (error) {
      console.log('Error saving instruction:', error);
      setSaveText('Save');
    }
  }

  async function saveInstructionWithContent(filteredContent) {
    setSaveText('Saving...');
    try {
      if (typeof onSave === 'function') {
        await onSave(filteredContent, remoteTextRef.current);
      } else {
        const saveAction = isLibraryConfig
          ? apiClient.editLibraryConfigInstruction(
              configID,
              emrFromUrl,
              section,
              filteredContent,
            )
          : apiClient.editConfig(
              configID,
              'update_question_value',
              sectionText,
              filteredContent,
            );

        const data = await saveAction;
        if ('error' in data) {
          console.log('Error editing config:', data.error);
          return;
        }
      }
      setSaveText('Save');
      // Extract TopLevelInstruction from filteredContent for onChange
      let instructionText = filteredContent;
      try {
        const parsed = JSON.parse(filteredContent);
        if (parsed.TopLevelInstruction) {
          instructionText = parsed.TopLevelInstruction;
        }
      } catch (error) {
        console.log('Failed to parse filteredContent, using as-is');
      }
      onChange(instructionText, remoteTextRef.current);
    } catch (error) {
      console.log('Error saving instruction:', error);
      setSaveText('Save');
    }
  }

  async function autoGenInstruction() {
    setConfirmModal(false);
    if (resetText === 'Thinking...') {
      return;
    }
    setResetText('Thinking...');

    let tempSectionText = sectionText || 'Something New!';
    try {
      let rsp = await apiClient.genTemplate(configID, section, true);

      if (rsp.error || rsp['result'] !== 'success') {
        const errorMessage = rsp.error || 'Unknown error occurred';
        console.log('Error: ', errorMessage);

        if (errorMessage.includes('Insufficient training data')) {
          setShowModal(true); // Show modal for insufficient data
          // EARLY RETURN to keep the existing instructions
          setResetText(magicWand);
          return;
        } else {
          setResetText('Try Again...');
        }
      }

      setResetText(magicWand);
      setLocalText(rsp.instruction || tempSectionText);
      onChange(rsp.instruction || tempSectionText, remoteTextRef.current);
    } catch (err) {
      //for handling timeout issues
      console.log('error', err);

      setResetText(magicWand);
      setLocalText(tempSectionText);
    }
  }

  const windowWidth = WindowWidthState();
  const textRef = useRef(null);

  // Handle pushing Edits
  function startEditing() {
    isEditingRef.current = true;
    // Prevent user from navigating away from the page while editing
    window.onbeforeunload = () => true;
  }
  function stopEditing() {
    isEditingRef.current = false;
    window.onbeforeunload = undefined;
    // Only push if the there is a change
    if (
      localTextRef.current !== remoteTextRef.current &&
      typeof onChange === 'function'
    ) {
      onChange(localTextRef.current, remoteTextRef.current);
    }
  }
  function setLocalText(value, push = false) {
    _setLocalText(value);
    if (push && value !== remoteTextRef.current) {
      onChange(value, remoteTextRef.current);
    }
  }

  // Handle Upstream edits (skip if user is actively editing to preserve unsaved changes)
  useEffect(() => {
    if (!isEditingRef.current) {
      setLocalText(text);
    }
  }, [text]);

  useEffect(() => {
    if (textRef.current) {
      textRef.current.style.height = '0px'; // Reset height before setting new value
      const newHeight = textRef.current.scrollHeight + 'px';
      textRef.current.style.height = newHeight;
    }
  }, [localText, windowWidth]);

  // Handle the unmount if if we uncollapse while editing
  useEffect(() => {
    return () => {
      // Make sure we push the changes if we are editing
      stopEditing();
    };
  }, []);

  return (
    <div
      className="editableLabel"
      {...restProps}
      style={{
        marginBottom: '10px',
        border: removeBorder
          ? 'none'
          : `3px solid ${disabled ? 'grey' : '$primary'}`,
        flexGrow: 1,
      }}
      onClick={(e) => {
        e.stopPropagation(); // keep collapsible from toggling
        if (disabled && typeof externalOnClick === 'function') {
          externalOnClick(e); // trigger view‑only modal when disabled
        }
      }}
    >
      <span
        className="labelText"
        style={{ color: disabled ? 'grey' : '$primary' }}
      >
        {labelText}
      </span>
      {!disabled ? (
        <>
          {typeof localText === 'object' && localText !== null ? (
            <TableEditor
              data={localText}
              onChange={setLocalText}
              startEditing={startEditing}
              stopEditing={stopEditing}
            />
          ) : (
            <textarea
              ref={(el) => {
                textRef.current = el;
                if (inputRef) inputRef.current = el;
              }}
              className={className}
              style={{
                width: '100%',
                minWidth: '150px', // Minimum width to avoid collapsing
                resize: 'none',
                overflow: 'auto', // Changed from 'hidden' to 'auto'
                maxHeight: '500px', // Max height after which the textarea becomes scrollable
              }}
              placeholder={placeholder}
              value={localText}
              onChange={(e) => {
                setLocalText(e.target.value);
              }}
              onBlur={() => {
                stopEditing();
                // Force save on blur for top-level instructions
                if (
                  typeof onChange === 'function' &&
                  localTextRef.current !== remoteTextRef.current
                ) {
                  onChange(localTextRef.current, remoteTextRef.current);
                }
              }}
              onFocus={(e) => {
                startEditing();
                const value = e.target.value;
                e.target.setSelectionRange(value.length, value.length); // Move cursor to end
              }}
            />
          )}
          {(isInstruction || forceShowSave) && (
            <div
              style={{
                display: 'flex',
                justifyContent: isInstruction ? 'space-between' : 'flex-end',
                padding: '2px',
                gap: '8px',
              }}
            >
              {isInstruction && (
                <button
                  style={{
                    fontSize: 'small',
                    color: '#888',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    setConfirmModal(true);
                  }}
                >
                  {resetText}
                </button>
              )}
              {isInstruction &&
                subsections &&
                Object.keys(subsections).length > 0 && (
                  <button
                    style={{
                      fontSize: 'small',
                      color: '#888',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={onToggleSubsections}
                  >
                    {showSubsections ? '▼ Hide all' : '▶ View More'}
                  </button>
                )}
              <button
                style={{
                  fontSize: 'small',
                  color: '#888',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={handleSave}
              >
                {saveText}
              </button>
            </div>
          )}
          <ConfirmationModal
            isOpen={ConfirmModal}
            onClose={handleCancel}
            onConfirm={handleConfirm}
            confirmButtonText="Confirm"
          >
            You are about to overwrite instructions and generate new one with AI
            based on provided examples
          </ConfirmationModal>
          <Modal
            isOpen={showModal}
            onClose={handleCancel}
            isCloseButton={false}
          >
            Insufficient examples. Please add at least 3 examples to generate
            instruction.
          </Modal>
        </>
      ) : (
        <>
          <div
            className={className}
            style={{
              whiteSpace: isSmallScreen ? 'pre-wrap' : 'pre-line', // Use pre-wrap for small screens, pre-line for others
              overflow: 'auto', // Enable scrolling
              textOverflow: 'ellipsis', // Adds ellipsis for overflowed text
              fontSize: isSmallScreen ? 'small' : 'lg', // Sets a smaller font size
              padding: isSmallScreen ? 'none' : '5px', // Adds padding for better spacing
              maxWidth: '100%', // Ensure the div doesn't exceed its parent width
              maxHeight: '500px', // Limit the height to enable scrolling
              wordBreak: 'break-word', // Break long words at the end of the line
            }}
          >
            {localText}
          </div>
          {isInstruction &&
            subsections &&
            Object.keys(subsections).length > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '2px',
                }}
              >
                <button
                  style={{
                    fontSize: 'small',
                    color: '#888',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={onToggleSubsections}
                >
                  {showSubsections ? '▼ Hide all' : '▶ View More'}
                </button>
              </div>
            )}
        </>
      )}
      {children}
    </div>
  );
};

export default EditableLabel;
